<script setup lang="ts">
import { humanizeErrorMessage, toCamelCase } from "@/utils/Formatters";
import { useField } from "vee-validate";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";

const props = defineProps<{
  name?: string;
  type?: string;
  label?: string;
  placeholder?: string;
  transparent?: boolean;
  disabled?: boolean;
  required?: boolean;
  waitingForBackend?: boolean;
  hideDetails?: boolean | "auto";
}>();

const fieldRef = ref<null | { focus: () => null }>(null);
const { smAndDown } = useDisplay();
const { t } = useI18n();
const { value, errorMessage } = useField(() => props.name ?? "");

function focus() {
  fieldRef.value?.focus();
}

defineExpose({ focus });
</script>

<template>
  <v-text-field
    ref="fieldRef"
    v-model="value"
    :class="{
      'v-input--transparent': props.transparent,
      'v-input--required': props.required,
      'v-input--waiting-for-backend': props.waitingForBackend,
    }"
    :bgColor="props.transparent ? 'transparent' : props.disabled ? 'surface/25' : 'surface'"
    :label="props.label ?? t(`generic.fields.${toCamelCase(props.name)}`)"
    :placeholder="props.placeholder"
    :hide-details="props.hideDetails ?? (smAndDown ? 'auto' : false)"
    :error-messages="
      humanizeErrorMessage(
        t,
        errorMessage,
        props.name,
        props.label ?? t(`generic.fields.${toCamelCase(props.name)}`)
      )
    "
    :type="props.type || 'text'"
    variant="outlined"
    density="comfortable"
    persistent-placeholder
    :disabled="props.disabled"
    :name="props.name"
  >
    <template
      v-for="slotName in Object.keys($slots)"
      :key="slotName"
    >
      <slot :name="slotName" />
    </template>
  </v-text-field>
</template>

<style src="@/assets/styles/components/fields.scss" scoped lang="scss" />
